import SegmentedBar from 'components/SegmentedBar';
import { useAtomValue } from 'jotai';
import React from 'react';
import { Card } from 'reactstrap';

import { findingsDataAtom } from './state';

const InsightItem: React.FC<{
  label: string;
  value: number;
  isLast?: boolean;
}> = ({ label, value, isLast = false }) => (
  <div
    className={`flex-grow-0 flex-shrink-0 border-right ${
      isLast ? '' : 'mr-2 pr-2'
    }`}
    style={{
      width: '120px',
      borderColor: '#dee2e6',
      ...(isLast && { borderRight: 'none' }), // Remove border for the last item
    }}
  >
    <div className="text-center">
      <h6 className="mb-0">{label}</h6>
      <h5 className="font-weight-bold mb-0">{value}</h5>
    </div>
  </div>
);

const FindingInsights: React.FC = () => {
  const listingData = useAtomValue(findingsDataAtom);
  const insights = listingData?.insights;

  if (!listingData || !insights) {
    return <div>Loading...</div>; // Add a loading state
  }

  // Extract stats from insights
  const stats = {
    total: insights.total,
    passed: insights.passed,
    failed: insights.failed,
    error: insights.error,
    exception: insights.exception,
    urgent: insights.urgent,
    critical: insights.critical,
    serious: insights.serious,
    medium: insights.medium,
    minimal: insights.minimal,
    uniqueControls: insights.unique_controls,
    uniqueAssets: insights.unique_assets,
    uniqueTechnologies: insights.unique_technologies,
    uniqueCategories: insights.unique_categories,
    uniquePolicies: insights.unique_policies,
    uniqueUnreadNotesCount: insights.unique_unread_notes_count,
    watchingByMeCount: insights.watching_by_me_count,
  };

  return (
    <div className="container-fluid border border-light rounded p-0 mb-1">
      <Card
        className="mb-0 px-3 py-1"
        style={{ backgroundColor: 'rgba(34, 42, 66, 0.02)' }}
      >
        <div className="d-flex flex-wrap align-items-center">
          {/* Compliance Status */}
          <div
            className="mr-2 pr-2 flex-grow-1 border-right"
            style={{ borderColor: '#dee2e6' }}
          >
            <h6 className="mb-0">Status:</h6>
            <SegmentedBar
              height="5px"
              width="100%"
              segments={[
                {
                  color: 'danger' as const,
                  value: stats.failed,
                  label: 'Failed',
                },
                {
                  color: 'success' as const,
                  value: stats.passed,
                  label: 'Passed',
                },
                ...(stats.error > 0
                  ? [
                      {
                        color: 'warning' as const,
                        value: stats.error,
                        label: 'Error',
                      },
                    ]
                  : []),
                ...(stats.exception > 0
                  ? [
                      {
                        color: 'dark' as const,
                        value: stats.exception,
                        label: 'Exception',
                      },
                    ]
                  : []),
              ]}
              labelDisplay="value"
              tooltip={`Failed: ${stats.failed}, Passed: ${stats.passed}${
                stats.error > 0 ? `, Error: ${stats.error}` : ''
              }${stats.exception > 0 ? `, Exception: ${stats.exception}` : ''}`}
            />
          </div>

          {/* Criticality */}
          <div
            className="mr-2 pr-2 flex-grow-1 border-right"
            style={{ borderColor: '#dee2e6' }}
          >
            <h6 className="mb-0">Criticality:</h6>
            <SegmentedBar
              height="5px"
              width="100%"
              segments={[
                {
                  color: 'urgent',
                  value: stats.urgent,
                  label: 'Urgent',
                },
                {
                  color: 'critical',
                  value: stats.critical,
                  label: 'Critical',
                },
                {
                  color: 'serious',
                  value: stats.serious,
                  label: 'Serious',
                },
                {
                  color: 'medium',
                  value: stats.medium,
                  label: 'Medium',
                },
                {
                  color: 'minimal',
                  value: stats.minimal,
                  label: 'Minimal',
                },
              ]}
              labelDisplay="value"
              tooltip={`Urgent: ${stats.urgent}, Critical: ${stats.critical}, Serious: ${stats.serious}, Medium: ${stats.medium}, Minimal: ${stats.minimal}`}
            />
          </div>

          {/* Count Summaries */}
          <InsightItem label="Assets" value={stats.uniqueAssets} />
          <InsightItem label="Policies" value={stats.uniquePolicies} />
          <InsightItem label="Controls" value={stats.uniqueControls} />
          <InsightItem label="Technologies" value={stats.uniqueTechnologies} />
          <InsightItem label="Categories" value={stats.uniqueCategories} />
          <InsightItem label="Watching" value={stats.watchingByMeCount} />
          <InsightItem
            label="Unread Notes"
            value={stats.uniqueUnreadNotesCount}
            isLast={true} // Mark the last item
          />
        </div>
      </Card>
    </div>
  );
};

export default FindingInsights;
